import React from 'react'
import './WhyChooseUs.scss'

// Real, verifiable Google listing (4.7 stars, 41 reviews) — see the Testimonials
// section. No invented counts or claims anywhere in this section.
const GOOGLE_REVIEWS_URL = 'https://www.google.com/maps?cid=14744859779437648009'

const GoogleG = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" style={{ display: 'block', flexShrink: 0 }}>
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
)

const Stars = ({ rating = 4.7 }) => {
  const pct = Math.max(0, Math.min(100, (rating / 5) * 100))
  return (
    <span className="why-choose-us__stars" aria-hidden="true">
      <span className="why-choose-us__stars-bg">★★★★★</span>
      <span className="why-choose-us__stars-fill" style={{ width: `${pct}%` }}>★★★★★</span>
    </span>
  )
}

const Arrow = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
  </svg>
)

// Rider-focused reasons (all genuine capabilities), with clean inline icons.
const REASONS = [
  {
    title: 'Fixed, all-inclusive fares',
    text: 'What you see upfront is what you pay. GST and driver allowance included, no surge, no haggling.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4" /><path d="M4 6v12a2 2 0 0 0 2 2h14v-4" /><path d="M18 12a2 2 0 0 0 0 4h4v-4Z" />
      </svg>
    ),
  },
  {
    title: 'Verified, professional drivers',
    text: 'Background-checked, well-rated and courteous, on every single trip.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><polyline points="16 11 18 13 22 9" />
      </svg>
    ),
  },
  {
    title: 'On-time pickups',
    text: 'Reliable pickup and drop, so you are never left waiting.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
      </svg>
    ),
  },
  {
    title: '24/7 customer support',
    text: 'Real people to help before, during and after your ride.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 14v-2a9 9 0 0 1 18 0v2" /><path d="M21 15a2 2 0 0 1-2 2h-1a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h3z" /><path d="M3 15a2 2 0 0 0 2 2h1a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1H3z" />
      </svg>
    ),
  },
  {
    title: 'Clean, comfortable cars',
    text: 'Well-maintained AC vehicles, kept clean for every journey.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 17h14M5 17a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h1l2-3h8l2 3h1a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2M5 17v2m14-2v2" /><circle cx="7.5" cy="17" r="0.5" /><circle cx="16.5" cy="17" r="0.5" />
      </svg>
    ),
  },
  {
    title: 'Live trip tracking',
    text: 'Share your ride status with family for complete peace of mind.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="3 11 22 2 13 21 11 13 3 11" />
      </svg>
    ),
  },
]

const WhyChooseUs = () => {
  const scrollToSearch = () => {
    document.querySelector('.home-main')?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <section className="why-choose-us">
      <div className="why-choose-us__container">
        <div className="why-choose-us__split">
          {/* Left: intro + real trust + CTA */}
          <div className="why-choose-us__intro">
            <span className="why-choose-us__eyebrow">Why Pulpit</span>
            <h2 className="why-choose-us__heading">
              Why riders choose <span>Pulpit</span>
            </h2>
            <p className="why-choose-us__lead">
              Fixed prices, verified drivers and support that actually answers. Here is what makes every
              Pulpit ride dependable, from a quick city hop to a long outstation run.
            </p>

            <a className="why-choose-us__rating" href={GOOGLE_REVIEWS_URL} target="_blank" rel="noreferrer">
              <GoogleG size={22} />
              <span className="why-choose-us__rating-num">4.7</span>
              <Stars rating={4.7} />
              <span className="why-choose-us__rating-count">41 Google reviews</span>
            </a>

            <button type="button" className="why-choose-us__cta" onClick={scrollToSearch}>
              Book a cab <Arrow />
            </button>
          </div>

          {/* Right: reasons as clean icon rows */}
          <ul className="why-choose-us__list">
            {REASONS.map((r) => (
              <li className="wcu-row" key={r.title}>
                <span className="wcu-row__icon">{r.icon}</span>
                <div className="wcu-row__body">
                  <h3 className="wcu-row__title">{r.title}</h3>
                  <p className="wcu-row__text">{r.text}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  )
}

export default WhyChooseUs
